import React, { useEffect, useState } from "react"
import { Box, Button, Heading, Input, Text } from "@theme-ui/components"
import { Link } from "theme-ui"
import ReactPaginate from "react-paginate"
import { buildClient } from "@datocms/cma-client-browser"
import { i18nContext } from "../context/i18nContext"

const client = buildClient({ apiToken: "45f7347c7fec1822c7be28c5d95e55" })

const resultsPerPage = 10

// Max results the API returns per call (its documented ceiling).
const fetchLimit = 100

// How many of those calls we're willing to make. Results come back in
// descending score order, so we stop as soon as a batch ends below the
// relevance threshold — in practice one call covers most queries, and this
// ceiling only applies to very broad ones.
const maxFetches = 3

// Fuzzy matching (Levenshtein) also returns a long tail of near-zero-relevance
// pages — on this site almost every page mentions "AB" or "biogas" somewhere, so
// a fuzzy query reports the whole index. Scores fall off a cliff (e.g. 2.0 -> 0.005)
// between genuine matches and that tail, so we keep only results scoring at least
// this fraction of the best hit. Any cut-off between 1% and 50% yields the same
// result set on real queries, so this filters noise rather than tuning ranking.
const minRelevance = 0.01

// Highlight markers come back as [h]match[/h]; render them as <mark> without
// injecting raw HTML. Odd indexes are the matched fragments.
const highlight = text =>
  (text || "")
    .split(/\[h\](.+?)\[\/h\]/g)
    .map((chunk, index) =>
      index % 2 === 1 ? (
        <mark key={`h${index}-${chunk}`}>{chunk}</mark>
      ) : (
        <span key={`t${index}-${chunk}`}>{chunk}</span>
      )
    )

// Indexed pages can have a null title (seen on some locales), and titles are
// suffixed with "| Gruppo AB" which we don't want to repeat on every row.
const pageTitle = title => (title || "").split("|")[0]

const SearchResults = ({ locale }) => {
  const [query, setQuery] = useState("")
  const [currentQuery, setCurrentQuery] = useState("")
  const [results, setResults] = useState([])
  const [page, setPage] = useState(0)
  const [status, setStatus] = useState("idle")

  const handleSubmit = event => {
    event.preventDefault()
    setPage(0)
    setCurrentQuery(query)
  }

  useEffect(() => {
    if (!currentQuery) {
      setResults([])
      setStatus("idle")
      return
    }

    let cancelled = false
    setStatus("loading")
    // Reset paging here too, not just on submit: a shorter result set would
    // otherwise leave us on a page that no longer exists.
    setPage(0)

    const search = async () => {
      const matches = []
      let threshold = 0

      for (let fetched = 0; fetched < maxFetches; fetched++) {
        const response = await client.searchResults.rawList({
          filter: { query: currentQuery, fuzzy: true, locale },
          page: { limit: fetchLimit, offset: fetched * fetchLimit },
        })

        if (cancelled || response.data.length === 0) break

        if (fetched === 0) {
          // A top score of 0 would make the threshold 0 and let everything
          // through, defeating the filter — keep it above zero.
          threshold =
            response.data[0].attributes.score * minRelevance || Number.MIN_VALUE
        }

        matches.push(
          ...response.data.filter(
            result => result.attributes.score >= threshold
          )
        )

        // Results are ordered by descending score, so once a batch ends below
        // the threshold there is nothing relevant left to fetch.
        const lastScore =
          response.data[response.data.length - 1].attributes.score
        if (lastScore < threshold || response.data.length < fetchLimit) break
      }

      if (cancelled) return
      setResults(matches)
      setStatus("done")
    }

    search().catch(() => {
      if (cancelled) return
      setStatus("error")
    })

    return () => {
      cancelled = true
    }
  }, [currentQuery, locale])

  const pageCount = Math.ceil(results.length / resultsPerPage)
  // Guard against a stale page index outliving a smaller result set.
  const currentPage = Math.min(page, Math.max(pageCount - 1, 0))
  const pageResults = results.slice(
    currentPage * resultsPerPage,
    currentPage * resultsPerPage + resultsPerPage
  )

  return (
    <i18nContext.Consumer>
      {t => (
        <Box>
          <Box as="form" name="search" onSubmit={handleSubmit} sx={{ mb: 6 }}>
            <Input
              onChange={e => setQuery(e.target.value)}
              type="text"
              name="query"
              id="query"
              value={query}
              required
              sx={{ mb: 3, padding: 3, fontSize: 3 }}
            />
            <Button type="submit">{t.search}</Button>
          </Box>
          <Box
            sx={{
              ".pagination": {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                listStyle: "none",
                paddingLeft: 0,
                li: {
                  cursor: "pointer",
                  mx: 3,
                  "&.active": {
                    a: {
                      fontWeight: "bold",
                    },
                  },
                  "a:hover": {
                    color: "dark",
                  },
                },
              },
            }}
          >
            {status === "loading" && <Text>{t.search}…</Text>}
            {status === "error" && <Text>{t.noResults}</Text>}
            {status === "done" && results.length === 0 && (
              <Text>{t.noResults}</Text>
            )}
            {status === "done" && results.length > 0 && (
              <Box>
                <Heading sx={{ fontSize: 3, mb: 3 }}>
                  {currentQuery}: {results.length} {t.results}
                </Heading>
                {pageResults.map(result => (
                  <Box sx={{ mb: 4 }} key={result.id}>
                    <Link href={result.attributes.url}>
                      <Heading sx={{ color: "primary", fontSize: 5, mb: 3 }}>
                        {result.attributes.highlight?.title
                          ? highlight(
                              pageTitle(result.attributes.highlight.title[0])
                            )
                          : pageTitle(result.attributes.title)}
                      </Heading>
                    </Link>
                    <Box>
                      {result.attributes.highlight?.body
                        ? highlight(result.attributes.highlight.body[0])
                        : result.attributes.body_excerpt}
                    </Box>
                  </Box>
                ))}
                {pageCount > 1 && (
                  <Box sx={{ mb: 6 }}>
                    <ReactPaginate
                      previousLabel={"<"}
                      nextLabel={">"}
                      breakLabel={"..."}
                      breakClassName={"break-me"}
                      pageCount={pageCount}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={5}
                      onPageChange={({ selected }) => setPage(selected)}
                      containerClassName={"pagination"}
                      activeClassName={"active"}
                      forcePage={currentPage}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </i18nContext.Consumer>
  )
}

export default SearchResults
